import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth/auth-context.js';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@tailoring-crm/ui';
import { CredentialForm } from './credential-form.js';

/**
 * "Your session has ended" — asked over the screen, never instead of it.
 *
 * A session dies at the worst possible moment by definition: the shop has been quiet for an
 * hour, somebody has just typed three garments and a promised date into a booking form, and
 * the refresh token has finally expired. Navigating to `/login` at that point is correct,
 * secure, and throws away twenty minutes of a person's afternoon.
 *
 * So the screen underneath stays mounted, with everything still typed into it, and this asks
 * for the credential over the top. Signing in again puts the session back and dismisses the
 * dialog; the form behind it never knew. The number is prefilled and locked because we know
 * exactly whose session ended — and letting someone sign in as a DIFFERENT person over a
 * half-finished order belonging to the first is how a booking ends up attributed to the wrong
 * staff member.
 *
 * It cannot be dismissed by clicking away. The only two exits are signing back in and signing
 * out, because a dialog you can escape from over an app whose every request now 401s is worse
 * than no dialog at all.
 */
export function SessionExpiredDialog(): ReactElement {
  const { t } = useTranslation();
  const auth = useAuth();
  const navigate = useNavigate();

  return (
    <Dialog open>
      <DialogContent
        // No close button, no outside dismissal, no escape. The X is a direct child of the
        // content element, so this hides that and nothing inside the form.
        className="[&>button]:hidden"
        onEscapeKeyDown={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{t('auth.expiredTitle')}</DialogTitle>
          <DialogDescription>{t('auth.expiredDescription')}</DialogDescription>
        </DialogHeader>

        <CredentialForm
          email={auth.user?.email ?? ''}
          lockEmail={auth.user !== null}
          submitLabel={t('auth.signIn')}
          onAuthenticated={(result) => {
            // A person with two shops re-authenticating mid-order has no shop to choose: they
            // are already IN one. Sending them to the picker would risk swapping the shop out
            // from under the screen behind this dialog, so that case goes to a clean sign-in.
            if (result.status === 'select_shop') {
              void (async () => {
                await auth.signOut();
                void navigate('/login', { replace: true });
              })();
              return;
            }
            auth.signIn({
              user: result.user,
              shop: result.shop,
              role: result.role,
              sessionId: result.sessionId,
              memberships: auth.memberships,
            });
          }}
        >
          <Button
            variant="ghost"
            block
            onClick={() => {
              void (async () => {
                await auth.signOut();
                void navigate('/login', { replace: true });
              })();
            }}
          >
            {t('account.signOut')}
          </Button>
        </CredentialForm>
      </DialogContent>
    </Dialog>
  );
}
